import React, { useState, useMemo, useEffect } from 'react';
import PolymerBarChart from '@/components/charts/PolymerBarChart';
import PolymerLineChart from '@/components/charts/PolymerLineChart';
import { getRsColors } from '@/utils/chartColors';
import { CheckboxDropdown } from '@/components/common/CheckboxDropdown';
import { useTheme } from '@/contexts/ThemeContext';

const COMMODITIES = [
  { key: 'spot_ethylene', label: 'Spot Ethylene' },
  { key: 'ethane', label: 'Ethane' },
  { key: 'propane', label: 'Propane' },
  { key: 'spot_rgp', label: 'Spot RGP' },
  { key: 'spot_pgp', label: 'Spot PGP' },
  { key: 'spot_benzene', label: 'Spot Benzene' },
  { key: 'wti_crude_oil', label: 'WTI Crude' },
  { key: 'nat_gas', label: 'Nat. Gas' },
];

type Period = 'year' | 'month' | 'week';

interface PetchemAveragesProps {
  petchemData: any[];
  showDetails?: boolean;
}

function computeAverages(data: any[], key: string, period: Period): { labels: string[]; values: number[] } {
  const map = new Map<string, number[]>();

  for (const row of data) {
    if (row[key] === null || row[key] === undefined) continue;
    const d = new Date(String(row.date).slice(0, 10) + 'T00:00:00');
    if (isNaN(d.getTime())) continue;
    let bucket: string;
    if (period === 'year') {
      bucket = String(d.getFullYear());
    } else if (period === 'month') {
      bucket = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    } else {
      const jan1 = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil(((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
      bucket = `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
    }
    if (!map.has(bucket)) map.set(bucket, []);
    map.get(bucket)!.push(Number(row[key]));
  }

  const entries = Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  return {
    labels: entries.map(([k]) => k),
    values: entries.map(([, vals]) => vals.reduce((a, b) => a + b, 0) / vals.length),
  };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toDisplayLabel(label: string, period: Period): string {
  if (period === 'year') return label;
  if (period === 'month') {
    const [y, m] = label.split('-').map(Number);
    return `${MONTH_NAMES[m - 1]} ${y}`;
  }
  const wm = label.match(/^(\d{4})-W(\d{2})$/);
  if (wm) {
    const year = Number(wm[1]);
    const week = Number(wm[2]);
    const jan4 = new Date(year, 0, 4);
    const dow = jan4.getDay() || 7;
    const weekStart = new Date(jan4);
    weekStart.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
    return `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getFullYear()}`;
  }
  return label;
}

function toWeekMondayDate(label: string): string {
  const wm = label.match(/^(\d{4})-W(\d{2})$/);
  if (!wm) return label;
  const year = Number(wm[1]);
  const week = Number(wm[2]);
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay() || 7;
  const weekStart = new Date(jan4);
  weekStart.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  const y = weekStart.getFullYear();
  const m = String(weekStart.getMonth() + 1).padStart(2, '0');
  const d = String(weekStart.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const PERIOD_LABELS: Record<Period, string> = {
  year: 'Year',
  month: 'Month',
  week: 'Week',
};

const TABLE_HEADS: Record<Period, string> = {
  year: 'Year',
  month: 'Month',
  week: 'Week',
};

const PetchemAverages: React.FC<PetchemAveragesProps> = ({ petchemData, showDetails = true }) => {
  const { isDark } = useTheme();
  const [selected, setSelected] = useState('spot_ethylene');
  const [period, setPeriod] = useState<Period>('year');
  const [isSmallAverages, setIsSmallAverages] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 750px)');
    setIsSmallAverages(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmallAverages(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const rs = useMemo(() => getRsColors(), []);
  const selectedLabel = COMMODITIES.find((c) => c.key === selected)?.label || selected;

  const { labels, values } = useMemo(
    () => computeAverages(petchemData, selected, period),
    [petchemData, selected, period],
  );

  const lineDatasets = useMemo(
    () => [{ label: selectedLabel, data: values, color: rs.green || '#80ed40' }],
    [selectedLabel, values, rs.green],
  );

  const displayLabels = useMemo(
    () => labels.map((l) => toDisplayLabel(l, period)),
    [labels, period],
  );

  // ~4 months of weekly points (17 weeks)
  const WEEKLY_DEFAULT_POINTS = 17;
  // 12 months default for monthly
  const MONTHLY_DEFAULT_POINTS = 12;

  return (
    <div className={`flex flex-col gap-4 ${isSmallAverages ? 'p-0 -mx-6' : ''}`}>
      {/* Controls row */}
      <div className={`flex flex-wrap items-center gap-4 ${isSmallAverages ? 'justify-center px-4' : ''}`}>
        <CheckboxDropdown
          label="Commodity"
          options={COMMODITIES.map((c) => c.label)}
          selected={new Set([COMMODITIES.find((c) => c.key === selected)?.label || ''])}
          onChange={(selected) => {
            const label = Array.from(selected)[0];
            const commodity = COMMODITIES.find((c) => c.label === label);
            if (commodity) setSelected(commodity.key);
          }}
          singleSelect={true}
          hideCount={true}
          shiftMenuRight={true}
        />
        <div className="flex gap-1.5">
          {(['year', 'month', 'week'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors capitalize ${
                period === p ? 'bg-rs-green text-black' : 'bg-rs-jet text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className={`border border-rs-jet-light rounded-lg ${isSmallAverages ? 'p-0' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallAverages ? 'px-4 pt-4 text-center' : ''}`}>
          {selectedLabel} — {PERIOD_LABELS[period]}ly Averages
        </h3>
        {petchemData.length === 0 ? (
          <div className="h-[700px] flex items-center justify-center text-rs-off-white/40 text-sm">No data</div>
        ) : (
          <div className={`h-[700px] ${isSmallAverages ? 'px-4 pb-4' : ''}`}>
            {period === 'year' ? (
              <PolymerBarChart
                data={values}
                labels={labels}
                label={selectedLabel}
                showLabels
                showRangeSlider={false}
                color={rs.green || '#80ed40'}
                dataLabelColor={isDark ? '#000000' : undefined}
                className="w-full h-full"
              />
            ) : (
              <PolymerLineChart
                key={`${selected}-${period}`}
                datasets={lineDatasets}
                labels={displayLabels}
                showRangeSlider
                showDataLabels={false}
                compact
                defaultVisiblePoints={period === 'week' ? WEEKLY_DEFAULT_POINTS : MONTHLY_DEFAULT_POINTS}
                className="w-full h-full"
              />
            )}
          </div>
        )}
      </div>

      {/* Details table */}
      {showDetails && labels.length > 0 && (
        <div className={`border border-rs-jet-light rounded-lg overflow-hidden ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
          <div className="px-4 py-2 bg-rs-jet/60">
            <h4 className="text-rs-off-white/70 text-xs uppercase tracking-wider">
              {PERIOD_LABELS[period]}ly Detail — {selectedLabel}
            </h4>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-rs-jet">
                <tr>
                  <th className="px-4 py-2 text-left text-rs-off-white/60">{TABLE_HEADS[period]}</th>
                  <th className="px-4 py-2 text-right text-rs-off-white/60">Avg ({selectedLabel})</th>
                </tr>
              </thead>
              <tbody>
                {[...labels].reverse().map((lbl, i) => {
                  const reverseIdx = labels.length - 1 - i;
                  return (
                    <tr key={lbl} className={`border-t border-rs-jet-light/40 ${i % 2 === 0 ? 'bg-rs-off-black/30' : ''}`}>
                      <td className="px-4 py-1.5 text-rs-off-white/80">{period === 'week' ? toWeekMondayDate(lbl) : lbl}</td>
                      <td className={`px-4 py-1.5 text-right font-mono ${isDark ? 'text-rs-green' : 'text-emerald-700'}`}>
                        {values[reverseIdx] !== undefined ? values[reverseIdx].toFixed(4) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetchemAverages;
